import React, { useState, useEffect } from "react";
import { Offcanvas, OffcanvasHeader, OffcanvasBody, Input } from "reactstrap";
import SimpleBar from "simplebar-react";
import { useSelector, useDispatch } from "react-redux";
import { createSelector } from "reselect";
import moment from "moment-timezone";
import "moment/locale/mn";
import Loader from "../../Components/Common/Loader";

moment.updateLocale("mn", {
  relativeTime: {
    future: "%s дараа",
    past: "%s өмнө",
    s: "хэдхэн секундын",
    ss: "%d секундын",
    m: "1 минутын",
    mm: "%d минутын",
    h: "1 цагийн",
    hh: "%d цагийн",
    d: "1 өдрийн",
    dd: "%d өдрийн",
    M: "1 сарын",
    MM: "%d сарын",
    y: "1 жилийн",
    yy: "%d жилийн",
  },
});

import dummyImg from "../../assets/images/users/user-dummy-img.jpg";
import { getUsers as onGetUsers } from "../../slices/thunks";

const ActiveUsers = () => {
  const dispatch = useDispatch();
  const [isRight, setIsRight] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [showLoader, setShowLoader] = useState(true);

  const selectLayoutState = (state) => state.Crm;
  const crmuserData = createSelector(selectLayoutState, (state) => ({
    crmusers: state.crmusers,
    isUserSuccess: state.isUserSuccess,
    error: state.error,
  }));

  const { crmusers, isUserSuccess, error } = useSelector(crmuserData);

  useEffect(() => {
    dispatch(onGetUsers());
    const interval = setInterval(() => {
      dispatch(onGetUsers());
    }, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [dispatch]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setShowLoader(false);
    }, 90000);

    return () => clearTimeout(timer);
  }, []);

  const toggleRightCanvas = () => setIsRight(!isRight);

  const isOnline = (lastActive) => {
    if (!lastActive) return false;
    const diff = (new Date() - new Date(lastActive)) / 1000 / 60;
    return diff <= 5;
  };

  const formatLastActive = (time) => {
    if (!time) return "Offline";
    return moment.tz(time, "Asia/Ulaanbaatar").fromNow();
  };

  const filteredUsers = crmusers
    ?.filter((user) => {
      const name = `${user.first_name} ${user.last_name}`.toLowerCase();
      const company = (user.company || "").toLowerCase();
      const term = searchTerm.toLowerCase();
      return name.includes(term) || company.includes(term);
    })
    .sort((a, b) => isOnline(b.lastActive) - isOnline(a.lastActive));

  const onlineCount = crmusers?.filter((u) => isOnline(u.lastActive)).length;

  const formatName = (user) => {
    const firstInitial = user.last_name ? user.last_name[0].toUpperCase() : "";
    return `${firstInitial}.${user.first_name}`;
  };

  return (
    <React.Fragment>
      <div className="ms-1 header-item d-none d-sm-flex">
        <button
          onClick={toggleRightCanvas}
          type="button"
          className="btn btn-icon btn-topbar btn-ghost-secondary rounded-circle"
        >
          <i className="ri-team-fill fs-22"></i>
          <span className="position-absolute cartitem-badge topbar-badge fs-10 translate-middle badge rounded-pill bg-primary">
            {onlineCount}
          </span>
        </button>
      </div>

      <Offcanvas
        isOpen={isRight}
        direction="end"
        toggle={toggleRightCanvas}
        id="offcanvasRight"
        className="border-bottom"
      >
        <OffcanvasHeader toggle={toggleRightCanvas} id="offcanvasRightLabel">
          Онлайн хэрэглэгчид
        </OffcanvasHeader>
        <OffcanvasBody className="p-0 overflow-hidden">
          <div className="p-2 border-bottom">
            <Input
              type="text"
              placeholder="Хайх..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          {isUserSuccess && filteredUsers.length ? (
            <SimpleBar style={{ height: "calc(100vh - 60px)" }}>
              <div className="acitivity-timeline p-3">
                {filteredUsers.map((user) => {
                  const online = isOnline(user.lastActive);
                  return (
                    <div
                      key={user._id}
                      className="acitivity-item d-flex align-items-center mb-3 p-2 rounded shadow-sm"
                      style={{
                        background: online ? "#e6ffed" : "#f8f9fa",
                        borderLeft: online
                          ? "4px solid #4caf50"
                          : "4px solid #ccc",
                      }}
                    >
                      <div className="flex-shrink-0">
                        <img
                          src={user.image_url || dummyImg}
                          alt={user.first_name}
                          className="avatar-xs rounded-circle"
                          style={{
                            border: online
                              ? "2px solid #4caf50"
                              : "2px solid #ccc",
                          }}
                        />
                      </div>
                      <div className="flex-grow-1 ms-3">
                        <h6 className="mb-1 d-flex align-items-center">
                          {formatName(user)}{" "}
                          {online && (
                            <span
                              className="ms-2"
                              style={{
                                height: "10px",
                                width: "10px",
                                backgroundColor: "#4caf50",
                                borderRadius: "50%",
                                display: "inline-block",
                              }}
                              title="Online"
                            ></span>
                          )}
                        </h6>

                        {!online && (
                          <small className="text-muted d-block mb-1 d-flex align-items-center">
                            <i className="ri-time-line me-1"></i>
                            {formatLastActive(user.lastActive)}
                          </small>
                        )}

                        <small className="text-muted d-block mb-1 d-flex align-items-center">
                          <i className="ri-building-line me-1"></i>
                          {user.company}
                        </small>

                        <small className="text-muted d-block mb-1 d-flex align-items-center">
                          <i className="ri-suitcase-line me-1"></i>
                          {user.role}
                        </small>
                      </div>
                    </div>
                  );
                })}
                {filteredUsers.length === 0 && (
                  <div className="text-center text-muted mt-3">
                    Хайлтанд тохирох хэрэглэгч олдсонгүй
                  </div>
                )}
              </div>
            </SimpleBar>
          ) : !showLoader && filteredUsers.length === 0 ? (
            <div className="text-center py-4 text-muted">
              Мэдээлэл байхгүй байна.
            </div>
          ) : (
            <Loader error={error} />
          )}
        </OffcanvasBody>
      </Offcanvas>
    </React.Fragment>
  );
};

export default ActiveUsers;
